<template>
  <div id="app">
    <router-view />
    <SessionExpirationWarning ref="sessionExpirationWarning" />
    <FileUploadModal
      v-if="fileUploadState"
      :is-modal-active="fileUploadState.isModalActive"
      :modal-message="fileUploadState.modalMessage"
      :upload-progress="fileUploadState.uploadProgress"
      @cancel-upload="cancelUpload"
      @continue-navigation="continueNavigation"
    />
    <!-- As DialogProgrammatic APIs are no longuer available in Vue 3,
      need to define a global dialog so that AlertService can be called in any context
      -->
    <DialogService ref="dialogServiceRef" />
  </div>
</template>

<script lang="ts">
import loadApplications from "@/composable/applications/applications";
import SessionExpirationWarning from "@/views/SessionExpirationWarning.vue";
import { onMounted, provide, Ref, ref } from "vue";
import sessionService, { MinimalSessionExpirationWarning } from "@/services/SessionService";
import { Fetcher, LOCAL_STORAGE_AUTHENTICATED_USER } from "@/services/Fetcher";
import { RateLimitToastNotifier } from "@/services/RateLimitToastNotifier";
import { ApplicationService } from "@/services/rest/ApplicationService";
import { LoginService } from "@/services/rest/LoginService";
import { DataService } from "@/services/rest/DataService";
import { FileUpload, useFileUpload } from "./composable/data/uploadFile";
import FileUploadModal from "./components/datas/FileUploadModal.vue";
import DialogService from "@/components/common/DialogService.vue";
import { Locales } from "@/utils/LocaleUtils";
import services from "@/composable/services";
import router from "@/router";
import i18n from "@/i18n";

export default {
  // eslint-disable-next-line vue/component-definition-name-casing
  name: "app",
  components: {
    SessionExpirationWarning,
    FileUploadModal,
    DialogService,
  },
  setup() {
    const fileUpload: FileUpload = useFileUpload();
    const fileUploadState = fileUpload.state;
    const cancelUpload = fileUpload.cancelUpload;
    const continueNavigation = fileUpload.continueNavigation;

    provide("fileUpload", fileUpload);
    const sessionExpirationWarning: Ref<MinimalSessionExpirationWarning | null> = ref(null);
    provide("application:progress", loadApplications().progress);
    provide("application:applications", loadApplications().applications);
    provide("application:ownApplications", loadApplications().ownApplications);
    provide("application:hasOwnApplications", loadApplications().hasOwnApplications);
    provide("application:showOwnApplications", loadApplications().showOwnApplications);
    provide("application:otherApplications", loadApplications().otherApplications);
    provide("application:loadApplications", loadApplications().loadApplications);

    const dialogServiceRef = ref(null);
    provide("dialogService", dialogServiceRef);

    onMounted(() => {
      sessionService.init(sessionExpirationWarning.value);
      Fetcher.configure({
        sessionTracker: sessionService,
        authStore: {
          getToken: () => Fetcher.AUTHORIZATION.value,
          setToken: (token) => {
            Fetcher.AUTHORIZATION.value = token;
          },
        },
        localeProvider: services.userPreferencesService,
        credentialsHandler: {
          notifyCredentialsLost: () => {
            const authenticatedUser = services.loginService.getStoredAuthenticatedUser();
            if (authenticatedUser && !authenticatedUser.state) {
              authenticatedUser.state = "idle";
            }
            services.loginService.setAuthenticateduser(authenticatedUser);
            localStorage.removeItem(LOCAL_STORAGE_AUTHENTICATED_USER);
          },
        },
        disconnectedDispatcher: {
          dispatchDisconnected: () => {
            document.dispatchEvent(new CustomEvent("disconnected"));
          },
        },
        rateLimitNotifier: RateLimitToastNotifier.INSTANCE,
      });
      ApplicationService.configureService({
        router,
        alertService: services.alertService,
        i18n: { t: (key: string) => i18n.global.t(key) },
      });
      LoginService.configureService({ router });
      DataService.configureService({ internationalisationService: services.internationalisationService });
      if (!localStorage.getItem("lang")) {
        const defaultLang = navigator.language.slice(0, 2) || Locales.FRENCH;
        localStorage.setItem("lang", defaultLang);
      }
    });
    return {
      sessionExpirationWarning,
      fileUploadState,
      cancelUpload,
      continueNavigation,
    };
  },
};
</script>

<style lang="scss">
#app {
  font-family: $font-text;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: $text-default-color;
  display: flex;
  flex-direction: column;
  height: 100%;
}

div.media-content span {
  white-space: pre-line;
  word-break: break-all;
}
</style>
